import { useMemo } from 'react';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  addMonths, 
  addWeeks, 
  subMonths,
  format 
} from 'date-fns';
import { fr } from 'date-fns/locale';

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
}

interface GanttTask {
  id: string;
  title: string;
  reference: string;
  startDate: Date;
  endDate: Date;
  type: 'redaction' | 'publication' | 'deadline' | 'qr' | 'analyse' | 'soutenance' | 'adjudication' | 'contractualisation';
  status: 'expired' | 'urgent' | 'upcoming';
  originalAO: AppelOffre;
}

type ViewMode = 'monthly' | 'weekly' | 'daily';

interface TimelineColumn {
  date: Date;
  label: string;
  fullLabel: string;
  type: 'day' | 'week' | 'month';
}

export const useGanttData = (
  appelOffres: AppelOffre[],
  selectedYear: number,
  viewStartDate: Date,
  viewEndDate: Date,
  viewMode: ViewMode
) => {
  // Transform AO data to Gantt tasks with memoization
  const ganttTasks = useMemo(() => {
    const tasks: GanttTask[] = [];
    const today = new Date();
    
    appelOffres
      .filter(ao => new Date(ao.date_limite).getFullYear() === selectedYear)
      .forEach((ao) => {
        const dateLimite = new Date(ao.date_limite);
        const datePublication = ao.date_publication ? new Date(ao.date_publication) : null;
        const dateDebutRedaction = ao.date_debut_redaction_ao ? new Date(ao.date_debut_redaction_ao) : null;
        
        // Determine status
        const diffDays = Math.ceil((dateLimite.getTime() - today.getTime()) / (1000 * 3600 * 24));
        let status: 'expired' | 'urgent' | 'upcoming' = 'upcoming';
        if (diffDays < 0) status = 'expired';
        else if (diffDays <= 7) status = 'urgent';

        // Add redaction phase if available
        if (dateDebutRedaction && datePublication) {
          tasks.push({
            id: `${ao.id}-redaction`,
            title: `${ao.titre} - Rédaction`,
            reference: ao.reference,
            startDate: dateDebutRedaction,
            endDate: datePublication,
            type: 'redaction',
            status,
            originalAO: ao
          });
        }

        // Add publication to deadline phase
        if (datePublication) {
          tasks.push({
            id: `${ao.id}-publication`,
            title: `${ao.titre} - Publication`,
            reference: ao.reference,
            startDate: datePublication,
            endDate: dateLimite,
            type: 'publication',
            status,
            originalAO: ao
          });
        } else {
          // If no publication date, create a task from estimated start to deadline
          const estimatedStart = dateDebutRedaction || subMonths(dateLimite, 1);
          tasks.push({
            id: `${ao.id}-full`,
            title: ao.titre,
            reference: ao.reference,
            startDate: estimatedStart,
            endDate: dateLimite,
            type: 'deadline',
            status,
            originalAO: ao
          });
        }

        // Add Q&R phase (from Q&R soumissionnaires to Réponses Q&R)
        const dateQR = ao.date_qr_soumissionnaires ? new Date(ao.date_qr_soumissionnaires) : null;
        const dateReponsesQR = ao.date_reponses_qr ? new Date(ao.date_reponses_qr) : null;
        if (dateQR && dateReponsesQR) {
          tasks.push({
            id: `${ao.id}-qr`,
            title: `${ao.titre} - Q&R`,
            reference: ao.reference,
            startDate: dateQR,
            endDate: dateReponsesQR,
            type: 'qr',
            status,
            originalAO: ao
          });
        }

        // Add Analyse phase (single date point)
        const dateAnalyse = ao.date_analyse_offres ? new Date(ao.date_analyse_offres) : null;
        if (dateAnalyse) {
          tasks.push({
            id: `${ao.id}-analyse`,
            title: `${ao.titre} - Analyse`,
            reference: ao.reference,
            startDate: dateAnalyse,
            endDate: dateAnalyse,
            type: 'analyse',
            status,
            originalAO: ao
          });
        }

        // Add Soutenance phase (from début to fin soutenance)
        const dateDebutSoutenance = ao.date_debut_soutenance ? new Date(ao.date_debut_soutenance) : null;
        const dateFinSoutenance = ao.date_fin_soutenance ? new Date(ao.date_fin_soutenance) : null;
        if (dateDebutSoutenance) {
          tasks.push({
            id: `${ao.id}-soutenance`,
            title: `${ao.titre} - Soutenance`,
            reference: ao.reference,
            startDate: dateDebutSoutenance,
            endDate: dateFinSoutenance || dateDebutSoutenance,
            type: 'soutenance',
            status,
            originalAO: ao
          });
        }

        // Add Adjudication (single date point)
        const dateAdjudication = ao.date_adjudication ? new Date(ao.date_adjudication) : null;
        if (dateAdjudication) {
          tasks.push({
            id: `${ao.id}-adjudication`,
            title: `${ao.titre} - Adjudication`,
            reference: ao.reference,
            startDate: dateAdjudication,
            endDate: dateAdjudication,
            type: 'adjudication',
            status,
            originalAO: ao
          });
        }

        // Add Contractualisation (single date point)
        const dateContractualisation = ao.date_contractualisation ? new Date(ao.date_contractualisation) : null;
        if (dateContractualisation) {
          tasks.push({
            id: `${ao.id}-contractualisation`,
            title: `${ao.titre} - Contractualisation`,
            reference: ao.reference,
            startDate: dateContractualisation,
            endDate: dateContractualisation,
            type: 'contractualisation',
            status,
            originalAO: ao
          });
        }
      });

    return tasks.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [appelOffres, selectedYear]);

  // Generate timeline columns based on view mode
  const timelineColumns = useMemo((): TimelineColumn[] => {
    switch (viewMode) {
      case 'daily':
        const days = eachDayOfInterval({ start: viewStartDate, end: viewEndDate });
        return days.map(day => ({
          date: day,
          label: format(day, 'd'),
          fullLabel: format(day, 'dd/MM/yyyy'),
          type: 'day' as const
        }));
        
      case 'weekly':
        const columns: TimelineColumn[] = [];
        let currentWeek = startOfWeek(viewStartDate, { weekStartsOn: 1 });
        const endWeek = endOfWeek(viewEndDate, { weekStartsOn: 1 });
        
        while (currentWeek <= endWeek) {
          columns.push({
            date: currentWeek,
            label: format(currentWeek, 'dd/MM'),
            fullLabel: `Semaine du ${format(currentWeek, 'dd/MM/yyyy')}`,
            type: 'week' as const
          });
          currentWeek = addWeeks(currentWeek, 1);
        }
        return columns;
        
      case 'monthly':
      default:
        const monthColumns: TimelineColumn[] = [];
        let currentMonth = startOfMonth(viewStartDate);
        const endMonth = endOfMonth(viewEndDate);
        
        while (currentMonth <= endMonth) {
          monthColumns.push({
            date: currentMonth,
            label: format(currentMonth, 'MMM'),
            fullLabel: format(currentMonth, 'MMMM yyyy', { locale: fr }),
            type: 'month' as const
          });
          currentMonth = addMonths(currentMonth, 1);
        }
        return monthColumns;
    }
  }, [viewStartDate, viewEndDate, viewMode]);

  // Calculate statistics
  const stats = useMemo(() => {
    const today = new Date();
    const yearAOs = appelOffres.filter(ao => new Date(ao.date_limite).getFullYear() === selectedYear);
    
    const total = yearAOs.length;
    const expired = yearAOs.filter(ao => new Date(ao.date_limite) < today).length;
    const urgent = yearAOs.filter(ao => {
      const diffDays = Math.ceil((new Date(ao.date_limite).getTime() - today.getTime()) / (1000 * 3600 * 24));
      return diffDays <= 7 && diffDays >= 0;
    }).length;

    return { total, expired, urgent };
  }, [appelOffres, selectedYear]);

  // Group tasks by AO ID
  const tasksByAO = useMemo(() => {
    const grouped = new Map<string, GanttTask[]>();
    ganttTasks.forEach(task => {
      const aoId = task.originalAO.id;
      if (!grouped.has(aoId)) {
        grouped.set(aoId, []);
      }
      grouped.get(aoId)!.push(task);
    });
    return grouped;
  }, [ganttTasks]);

  return {
    ganttTasks,
    timelineColumns,
    stats,
    tasksByAO,
  };
};